import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import ReactGA from 'react-ga4'
import { useNavigate } from 'react-router-dom'
import { slugify } from '../../utils/slugify'

const Card = styled(motion.div)`
    position: relative;
    background-color: ${({ theme }) => theme.card};
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    border: 1.5px solid rgba(255, 255, 255, 0.06);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    min-height: 300px;
    transition: border-color 0.35s ease,
                box-shadow 0.35s ease,
                transform 0.35s ease;

    &:hover {
        border-color: ${({ theme }) => theme.primary};
        box-shadow: 0 0 24px ${({ theme }) => theme.primary}33,
                    0 0 48px ${({ theme }) => theme.primary}15;
        transform: scale(1.03);
    }
`

const ImageArea = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 28px 24px 14px 24px;
    overflow: hidden;
`

const Image = styled.img`
    max-width: 92%;
    max-height: 210px;
    object-fit: contain;
    border-radius: 6px;
    transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.4));

    ${Card}:hover & {
        transform: scale(1.1);
    }
`

const BottomBar = styled.div`
    width: 100%;
    padding: 16px 18px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
`

const ProjectTitle = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_secondary};
    flex: 1;
    text-align: center;
    line-height: 1.35;
`

/* ─── "View Case Study" CTA ─── */
const CaseStudyCTA = styled.div`
    width: 100%;
    padding: 0 18px 16px;
    display: flex;
    justify-content: center;
`

const CTAButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.primary};
    padding: 6px 14px;
    border-radius: 20px;
    border: 1.5px solid ${({ theme }) => theme.primary}40;
    background: ${({ theme }) => theme.primary}0a;
    transition: all 0.3s ease;
    letter-spacing: 0.3px;

    ${Card}:hover & {
        background: ${({ theme }) => theme.primary}20;
        border-color: ${({ theme }) => theme.primary};
        transform: translateX(2px);
    }

    svg {
        width: 14px;
        height: 14px;
        transition: transform 0.3s ease;
    }

    ${Card}:hover & svg {
        transform: translateX(3px);
    }
`

// Hover overlay
const HoverOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 20px;
    background: rgba(0, 0, 0, 0.85);
    border-radius: 14px;
    opacity: 0;
    transition: opacity 0.3s ease;

    ${Card}:hover & {
        opacity: 1;
    }
`

const OverlayTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    text-align: center;
    line-height: 1.35;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
`

const OverlayDesc = styled.div`
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    text-align: center;
    line-height: 1.45;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
`

const OverlayTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: center;
`

const OverlayTag = styled.span`
    font-size: 9px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 2px 7px;
    border-radius: 10px;
`

const OverlayLinks = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 4px;
`

const LinkButton = styled.a`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 600;
    color: #ffffff;
    background: ${({ theme }) => theme.primary};
    padding: 7px 16px;
    border-radius: 20px;
    text-decoration: none;
    transition: background 0.3s ease, transform 0.2s ease;
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.primary}cc;
        transform: scale(1.06);
    }

    svg {
        width: 14px;
        height: 14px;
    }
`

const LinkButtonSecondary = styled(LinkButton)`
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.18);

    &:hover {
        background: rgba(255, 255, 255, 0.18);
    }
`

const ViewCaseStudyOverlayBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #ffffff;
    background: linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
    padding: 8px 18px;
    border-radius: 20px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 4px;

    &:hover {
        transform: scale(1.08);
        box-shadow: 0 4px 16px rgba(133, 76, 230, 0.4);
    }

    svg {
        width: 14px;
        height: 14px;
    }
`

const GitHubIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
    </svg>
)

const ExternalLinkIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
)

const ArrowRightIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"/>
        <polyline points="12 5 19 12 12 19"/>
    </svg>
)



const ProjectCards = ({project, setOpenModal, index = 0}) => {
    const navigate = useNavigate();

    const handleCardClick = (e) => {
        // Don't navigate if clicking an anchor link (GitHub, Demo, etc.)
        if (e.target.closest('a')) return;
        ReactGA.event({ category: 'Project', action: 'case_study_opened', label: project.title });
        navigate(`/projects/${slugify(project.title)}`);
    };

    const handleViewCaseStudy = (e) => {
        e.stopPropagation();
        ReactGA.event({ category: 'Project', action: 'case_study_cta_clicked', label: project.title });
        navigate(`/projects/${slugify(project.title)}`);
    };



    return (
        <Card
            onClick={handleCardClick}
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: (index % 4) * 0.08 }}
        >
            <ImageArea>
                <Image src={project.image} alt={project.title} loading="lazy" />
            </ImageArea>

            <BottomBar>
                <ProjectTitle>{project.title}</ProjectTitle>
            </BottomBar>

            {/* Visible CTA below card title — always visible, not just on hover */}
            <CaseStudyCTA>
                <CTAButton>
                    View Case Study
                    <ArrowRightIcon />
                </CTAButton>
            </CaseStudyCTA>

            <HoverOverlay>
                <OverlayTitle>{project.title}</OverlayTitle>
                <OverlayDesc>
                    {project.description?.split('\n')[0]?.substring(0, 140)}
                    {(project.description?.split('\n')[0]?.length > 140) && '...'}
                </OverlayDesc>
                <OverlayTags>
                    {project.tags?.slice(0, 4).map((tag, i) => (
                        <OverlayTag key={i}>{tag}</OverlayTag>
                    ))}
                    {project.tags?.length > 4 && (
                        <OverlayTag>+{project.tags.length - 4}</OverlayTag>
                    )}
                </OverlayTags>
                <OverlayLinks>
                    {project.github && (
                        <LinkButton
                            href={project.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <GitHubIcon />
                            GitHub
                        </LinkButton>
                    )}
                    {project.webapp && (
                        <LinkButtonSecondary
                            href={project.webapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <ExternalLinkIcon />
                            Demo
                        </LinkButtonSecondary>
                    )}
                </OverlayLinks>
                <ViewCaseStudyOverlayBtn onClick={handleViewCaseStudy}>
                    View Case Study
                    <ArrowRightIcon />
                </ViewCaseStudyOverlayBtn>
            </HoverOverlay>
        </Card>
    )
}

export default ProjectCards